'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PaybackPeriodCalculator() {
  const t = useTranslations("tools.paybackPeriodCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState<'fixed' | 'irregular'>('fixed');
  const [yearCount, setYearCount] = useState(5);
  const [showResults, setShowResults] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Fixed cash flow inputs
  const [fixedInputs, setFixedInputs] = useState({
    initialInvestment: 100000,
    cashFlow: 30000,
    cashFlowChange: 'increase' as 'increase' | 'decrease',
    changeRate: 5,
    years: 5,
    discountRate: 10,
  });

  // Irregular cash flow inputs
  const [irregularInputs, setIrregularInputs] = useState({
    initialInvestment: 100000,
    discountRate: 10,
    yearCashFlows: [5000, 25000, 35000, 40000, 30000],
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };
    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    success: '#059669',
    warning: '#f59e0b',
    error: '#dc2626',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '2rem' },
      { padding: '3rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    toolSubtitle: getResponsiveStyle(
      { color: colors.textSecondary },
      { fontSize: '0.875rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    tabsContainer: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '2rem',
      justifyContent: 'center',
      flexWrap: 'wrap' as const,
    },
    tabBtn: (isActive: boolean) => getResponsiveStyle(
      {
        background: isActive ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'white',
        color: isActive ? 'white' : colors.textSecondary,
        border: isActive ? 'none' : '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        fontWeight: 600,
        transition: 'all 0.3s ease',
      },
      { padding: '0.625rem 1.25rem', fontSize: '0.875rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    formGrid: {
      display: 'grid',
      gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '1.5rem',
      marginBottom: '1.5rem',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    label: getResponsiveStyle(
      { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    inputWrapper: {
      position: 'relative' as const,
    },
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        background: 'white',
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.875rem 1.25rem', fontSize: '1.125rem' },
      { padding: '0.875rem 1.25rem', fontSize: '1.125rem' }
    ),
    inputWithUnit: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        background: 'white',
        paddingRight: '3rem',
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.875rem 1.25rem', fontSize: '1.125rem' },
      { padding: '0.875rem 1.25rem', fontSize: '1.125rem' }
    ),
    inputUnit: {
      position: 'absolute' as const,
      right: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: colors.textSecondary,
      fontWeight: 600,
      pointerEvents: 'none' as const,
    },
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        background: 'white',
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.875rem 1.25rem', fontSize: '1.125rem' },
      { padding: '0.875rem 1.25rem', fontSize: '1.125rem' }
    ),
    cashFlowSection: {
      marginTop: '1.5rem',
    },
    cashFlowHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
      flexWrap: 'wrap' as const,
      gap: '0.5rem',
    },
    h4: getResponsiveStyle(
      { color: '#334155', margin: 0 },
      { fontSize: '1rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.1rem' }
    ),
    addYearBtn: getResponsiveStyle(
      {
        background: '#f1f5f9',
        border: 'none',
        borderRadius: '0.5rem',
        color: '#2563eb',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
      },
      { padding: '0.5rem 0.875rem', fontSize: '0.875rem' },
      { padding: '0.5rem 1rem', fontSize: '1rem' },
      { padding: '0.5rem 1rem', fontSize: '1rem' }
    ),
    yearInputRow: {
      display: 'grid',
      gridTemplateColumns: screenSize === 'mobile' ? '80px 1fr auto' : '100px 1fr auto',
      gap: '1rem',
      alignItems: 'center',
      marginBottom: '0.75rem',
    },
    yearLabel: getResponsiveStyle(
      { fontWeight: 600, color: colors.textSecondary },
      { fontSize: '0.875rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    removeYearBtn: getResponsiveStyle(
      {
        background: '#fee2e2',
        border: 'none',
        borderRadius: '0.5rem',
        color: colors.error,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
      },
      { padding: '0.5rem 0.625rem', fontSize: '0.75rem' },
      { padding: '0.5rem 0.75rem', fontSize: '0.875rem' },
      { padding: '0.5rem 0.75rem', fontSize: '0.875rem' }
    ),
    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1rem',
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' }
    ),
    errorMessage: getResponsiveStyle(
      {
        background: '#fee2e2',
        color: colors.error,
        borderRadius: '0.5rem',
        marginTop: '1rem',
        display: errorMessage ? 'block' : 'none',
      },
      { padding: '0.875rem', fontSize: '0.875rem' },
      { padding: '1rem', fontSize: '1rem' },
      { padding: '1rem', fontSize: '1rem' }
    ),
    resultsSection: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        display: showResults ? 'block' : 'none',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { color: colors.textPrimary, marginBottom: '1.5rem', textAlign: 'center' as const },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    resultGrid: {
      display: 'grid',
      gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    resultCard: (isHighlight: boolean) => getResponsiveStyle(
      {
        background: isHighlight ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '0.75rem',
        borderLeft: `4px solid ${isHighlight ? colors.warning : '#2563eb'}`,
      },
      { padding: '1.25rem' },
      { padding: '1.5rem' },
      { padding: '1.5rem' }
    ),
    resultLabel: getResponsiveStyle(
      { color: colors.textSecondary, fontWeight: 600, display: 'block', marginBottom: '0.5rem' },
      { fontSize: '0.875rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.9rem' }
    ),
    resultValue: (type?: 'success' | 'warning') => getResponsiveStyle(
      {
        color: type === 'success' ? colors.success : type === 'warning' ? colors.warning : colors.textPrimary,
        fontWeight: 700,
        display: 'block',
      },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '1.75rem' }
    ),
    resultTable: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      marginTop: '1.5rem',
      overflowX: 'auto' as const,
      display: 'block',
    },
    table: {
      width: '100%',
      minWidth: '600px',
    },
    th: getResponsiveStyle(
      {
        background: '#f8fafc',
        color: '#334155',
        fontWeight: 700,
        textAlign: 'right' as const,
        borderBottom: '1px solid #e2e8f0',
      },
      { padding: '0.625rem', fontSize: '0.75rem' },
      { padding: '0.75rem', fontSize: '0.9rem' },
      { padding: '0.75rem', fontSize: '0.9rem' }
    ),
    thFirst: getResponsiveStyle(
      {
        background: '#f8fafc',
        color: '#334155',
        fontWeight: 700,
        textAlign: 'left' as const,
        borderBottom: '1px solid #e2e8f0',
      },
      { padding: '0.625rem', fontSize: '0.75rem' },
      { padding: '0.75rem', fontSize: '0.9rem' },
      { padding: '0.75rem', fontSize: '0.9rem' }
    ),
    td: getResponsiveStyle(
      {
        color: colors.textSecondary,
        textAlign: 'right' as const,
        borderBottom: '1px solid #e2e8f0',
      },
      { padding: '0.625rem', fontSize: '0.75rem' },
      { padding: '0.75rem', fontSize: '0.875rem' },
      { padding: '0.75rem', fontSize: '0.875rem' }
    ),
    tdFirst: getResponsiveStyle(
      {
        color: colors.textSecondary,
        textAlign: 'left' as const,
        borderBottom: '1px solid #e2e8f0',
      },
      { padding: '0.625rem', fontSize: '0.75rem' },
      { padding: '0.75rem', fontSize: '0.875rem' },
      { padding: '0.75rem', fontSize: '0.875rem' }
    ),
    breakEvenRow: {
      background: '#fef3c7',
      fontWeight: 700,
    },
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', borderRadius: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.8, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
    },
    strong: {
      color: colors.textPrimary,
      fontWeight: 600,
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      { color: colors.textPrimary, fontWeight: 600, marginBottom: '0.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '0.875rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' }
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      transition: 'color 0.3s ease',
    },
  };

  const handleAddYear = () => {
    setIrregularInputs({
      ...irregularInputs,
      yearCashFlows: [...irregularInputs.yearCashFlows, 0],
    });
    setYearCount(yearCount + 1);
  };

  const handleRemoveYear = (index: number) => {
    const newCashFlows = irregularInputs.yearCashFlows.filter((_, i) => i !== index);
    setIrregularInputs({
      ...irregularInputs,
      yearCashFlows: newCashFlows,
    });
    setYearCount(yearCount - 1);
  };

  const handleYearCashFlowChange = (index: number, value: number) => {
    const newCashFlows = [...irregularInputs.yearCashFlows];
    newCashFlows[index] = value;
    setIrregularInputs({
      ...irregularInputs,
      yearCashFlows: newCashFlows,
    });
  };

  const calculateFixedCashFlow = () => {
    const { initialInvestment, cashFlow, cashFlowChange, changeRate, years, discountRate } = fixedInputs;

    if (initialInvestment <= 0 || cashFlow <= 0) {
      setErrorMessage(t('errorInvalidInputs'));
      setShowResults(false);
      return;
    }

    const cashFlows = [];
    let currentCashFlow = cashFlow;

    for (let i = 0; i < years; i++) {
      cashFlows.push(currentCashFlow);
      if (cashFlowChange === 'increase') {
        currentCashFlow *= (1 + changeRate / 100);
      } else {
        currentCashFlow *= (1 - changeRate / 100);
      }
    }

    displayResults(initialInvestment, cashFlows, discountRate / 100);
  };

  const calculateIrregularCashFlow = () => {
    const { initialInvestment, discountRate, yearCashFlows } = irregularInputs;

    if (initialInvestment <= 0) {
      setErrorMessage(t('errorInvalidInvestment'));
      setShowResults(false);
      return;
    }

    if (yearCashFlows.every(cf => cf === 0)) {
      setErrorMessage(t('errorNoCashFlows'));
      setShowResults(false);
      return;
    }

    displayResults(initialInvestment, yearCashFlows, discountRate / 100);
  };

  const displayResults = (initialInvestment: number, cashFlows: number[], discountRate: number) => {
    let cumulativeCashFlow = 0;
    let cumulativeDiscountedCashFlow = 0;
    let paybackPeriod: number | null = null;
    let discountedPaybackPeriod: number | null = null;

    const tableData = [];

    for (let i = 0; i < cashFlows.length; i++) {
      const year = i + 1;
      const cashFlow = cashFlows[i];
      const discountedCashFlow = cashFlow / Math.pow(1 + discountRate, year);

      cumulativeCashFlow += cashFlow;
      cumulativeDiscountedCashFlow += discountedCashFlow;

      const remainingBalance = initialInvestment - cumulativeCashFlow;
      const discountedRemainingBalance = initialInvestment - cumulativeDiscountedCashFlow;

      tableData.push({
        year,
        cashFlow,
        discountedCashFlow,
        cumulativeCashFlow,
        cumulativeDiscountedCashFlow,
        remainingBalance,
        discountedRemainingBalance,
      });

      if (paybackPeriod === null && cumulativeCashFlow >= initialInvestment) {
        const previousCumulative = cumulativeCashFlow - cashFlow;
        const fraction = (initialInvestment - previousCumulative) / cashFlow;
        paybackPeriod = i + fraction;
      }

      if (discountedPaybackPeriod === null && cumulativeDiscountedCashFlow >= initialInvestment) {
        const previousCumulative = cumulativeDiscountedCashFlow - discountedCashFlow;
        const fraction = (initialInvestment - previousCumulative) / discountedCashFlow;
        discountedPaybackPeriod = i + fraction;
      }
    }

    const totalCashFlow = cumulativeCashFlow;
    const averageReturn = ((totalCashFlow - initialInvestment) / initialInvestment) * 100;

    setResults({
      paybackPeriod,
      discountedPaybackPeriod,
      totalCashFlow,
      averageReturn,
      tableData,
    });

    setErrorMessage('');
    setShowResults(true);
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    if (activeTab === 'fixed') {
      calculateFixedCashFlow();
    } else {
      calculateIrregularCashFlow();
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolHeader}>
          <h2 style={styles.h2}>{t('toolTitle')}</h2>
          <p style={styles.toolSubtitle}>{t('toolSubtitle')}</p>
        </div>

        <div style={styles.tabsContainer}>
          <button
            style={styles.tabBtn(activeTab === 'fixed')}
            onClick={() => {
              setActiveTab('fixed');
              setShowResults(false);
              setErrorMessage('');
            }}
          >
            {t('tabFixed')}
          </button>
          <button
            style={styles.tabBtn(activeTab === 'irregular')}
            onClick={() => {
              setActiveTab('irregular');
              setShowResults(false);
              setErrorMessage('');
            }}
          >
            {t('tabIrregular')}
          </button>
        </div>

        <form style={styles.calculatorForm} onSubmit={handleCalculate}>
          {activeTab === 'fixed' ? (
            <div>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('labelInitialInvestment')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      style={styles.inputWithUnit}
                      value={fixedInputs.initialInvestment}
                      onChange={(e) => setFixedInputs({ ...fixedInputs, initialInvestment: parseFloat(e.target.value) || 0 })}
                      step="0.01"
                      required
                    />
                    <span style={styles.inputUnit}>$</span>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('labelAnnualCashFlow')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      style={styles.inputWithUnit}
                      value={fixedInputs.cashFlow}
                      onChange={(e) => setFixedInputs({ ...fixedInputs, cashFlow: parseFloat(e.target.value) || 0 })}
                      step="0.01"
                      required
                    />
                    <span style={styles.inputUnit}>$</span>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('labelCashFlowChange')}</label>
                  <select
                    style={styles.select}
                    value={fixedInputs.cashFlowChange}
                    onChange={(e) => setFixedInputs({ ...fixedInputs, cashFlowChange: e.target.value as 'increase' | 'decrease' })}
                  >
                    <option value="increase">{t('optionIncrease')}</option>
                    <option value="decrease">{t('optionDecrease')}</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('labelChangeRate')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      style={styles.inputWithUnit}
                      value={fixedInputs.changeRate}
                      onChange={(e) => setFixedInputs({ ...fixedInputs, changeRate: parseFloat(e.target.value) || 0 })}
                      step="0.01"
                    />
                    <span style={styles.inputUnit}>%</span>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('labelYears')}</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={fixedInputs.years}
                    onChange={(e) => setFixedInputs({ ...fixedInputs, years: parseInt(e.target.value) || 1 })}
                    min="1"
                    max="50"
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('labelDiscountRate')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      style={styles.inputWithUnit}
                      value={fixedInputs.discountRate}
                      onChange={(e) => setFixedInputs({ ...fixedInputs, discountRate: parseFloat(e.target.value) || 0 })}
                      step="0.01"
                    />
                    <span style={styles.inputUnit}>%</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('labelInitialInvestment')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      style={styles.inputWithUnit}
                      value={irregularInputs.initialInvestment}
                      onChange={(e) => setIrregularInputs({ ...irregularInputs, initialInvestment: parseFloat(e.target.value) || 0 })}
                      step="0.01"
                      required
                    />
                    <span style={styles.inputUnit}>$</span>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('labelDiscountRate')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      style={styles.inputWithUnit}
                      value={irregularInputs.discountRate}
                      onChange={(e) => setIrregularInputs({ ...irregularInputs, discountRate: parseFloat(e.target.value) || 0 })}
                      step="0.01"
                    />
                    <span style={styles.inputUnit}>%</span>
                  </div>
                </div>
              </div>

              <div style={styles.cashFlowSection}>
                <div style={styles.cashFlowHeader}>
                  <h4 style={styles.h4}>{t('labelAnnualCashFlows')}</h4>
                  <button type="button" style={styles.addYearBtn} onClick={handleAddYear}>
                    {t('btnAddYear')}
                  </button>
                </div>
                <div>
                  {irregularInputs.yearCashFlows.map((cashFlow, index) => (
                    <div key={index} style={styles.yearInputRow}>
                      <span style={styles.yearLabel}>{t('labelYear')} {index + 1}</span>
                      <div style={styles.inputWrapper}>
                        <input
                          type="number"
                          style={styles.inputWithUnit}
                          value={cashFlow}
                          onChange={(e) => handleYearCashFlowChange(index, parseFloat(e.target.value) || 0)}
                          step="0.01"
                        />
                        <span style={styles.inputUnit}>$</span>
                      </div>
                      {index >= 3 && (
                        <button type="button" style={styles.removeYearBtn} onClick={() => handleRemoveYear(index)}>
                          {t('btnRemove')}
                        </button>
                      )}
                      {index < 3 && <span style={{ width: screenSize === 'mobile' ? '60px' : '70px' }}></span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div style={styles.errorMessage}>{errorMessage}</div>

          <button type="submit" style={styles.calculateBtn}>
            {t('btnCalculate')}
          </button>
        </form>

        {showResults && results && (
          <div style={styles.resultsSection}>
            <h3 style={styles.resultsTitle}>{t('resultsTitle')}</h3>

            <div style={styles.resultGrid}>
              <div style={styles.resultCard(true)}>
                <span style={styles.resultLabel}>{t('resultPaybackPeriod')}</span>
                <span style={styles.resultValue(results.paybackPeriod ? 'success' : 'warning')}>
                  {results.paybackPeriod ? `${results.paybackPeriod.toFixed(2)} ${t('years')}` : t('notAchieved')}
                </span>
              </div>

              <div style={styles.resultCard(true)}>
                <span style={styles.resultLabel}>{t('resultDiscountedPayback')}</span>
                <span style={styles.resultValue(results.discountedPaybackPeriod ? 'success' : 'warning')}>
                  {results.discountedPaybackPeriod ? `${results.discountedPaybackPeriod.toFixed(2)} ${t('years')}` : t('notAchieved')}
                </span>
              </div>

              <div style={styles.resultCard(false)}>
                <span style={styles.resultLabel}>{t('resultTotalCashFlow')}</span>
                <span style={styles.resultValue()}>
                  ${formatCurrency(results.totalCashFlow)}
                </span>
              </div>

              <div style={styles.resultCard(false)}>
                <span style={styles.resultLabel}>{t('resultAverageReturn')}</span>
                <span style={styles.resultValue(results.averageReturn >= 0 ? 'success' : 'warning')}>
                  {results.averageReturn.toFixed(2)}%
                </span>
              </div>
            </div>

            <div style={styles.resultTable}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.thFirst}>{t('tableYear')}</th>
                    <th style={styles.th}>{t('tableCashFlow')}</th>
                    <th style={styles.th}>{t('tableDiscountedCF')}</th>
                    <th style={styles.th}>{t('tableCumulativeCF')}</th>
                    <th style={styles.th}>{t('tableCumulativeDiscCF')}</th>
                    <th style={styles.th}>{t('tableBalance')}</th>
                    <th style={styles.th}>{t('tableDiscBalance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.tableData.map((row: any, index: number) => {
                    const isPaybackYear = row.remainingBalance <= 0 && (row.year === 1 || results.tableData[row.year - 2].remainingBalance > 0);
                    const isDiscPaybackYear = row.discountedRemainingBalance <= 0 && (row.year === 1 || results.tableData[row.year - 2].discountedRemainingBalance > 0);
                    const rowStyle = (isPaybackYear || isDiscPaybackYear) ? styles.breakEvenRow : {};

                    return (
                      <tr key={index} style={rowStyle}>
                        <td style={styles.tdFirst}>{t('labelYear')} {row.year}</td>
                        <td style={styles.td}>${formatCurrency(row.cashFlow)}</td>
                        <td style={styles.td}>${formatCurrency(row.discountedCashFlow)}</td>
                        <td style={styles.td}>${formatCurrency(row.cumulativeCashFlow)}</td>
                        <td style={styles.td}>${formatCurrency(row.cumulativeDiscountedCashFlow)}</td>
                        <td style={styles.td}>${formatCurrency(row.remainingBalance)}</td>
                        <td style={styles.td}>${formatCurrency(row.discountedRemainingBalance)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introPara1')}</p>
        <p style={styles.paragraph}>{t('introPara2')}</p>
        <p style={styles.paragraph}>{t('introPara3')}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t('step1Title')}</span> {t('step1Desc')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('step2Title')}</span> {t('step2Desc')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('step3Title')}</span> {t('step3Desc')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('step4Title')}</span> {t('step4Desc')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('step5Title')}</span> {t('step5Desc')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('insight1')}</li>
          <li style={styles.listItem}>{t('insight2')}</li>
          <li style={styles.listItem}>{t('insight3')}</li>
          <li style={styles.listItem}>{t('insight4')}</li>
          <li style={styles.listItem}>{t('insight5')}</li>
          <li style={styles.listItem}>{t('insight6')}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <p style={styles.paragraph}>{t('detailsPara1')}</p>
        <p style={styles.paragraph}>{t('detailsPara2')}</p>
        <p style={styles.paragraph}>{t('detailsPara3')}</p>
        <p style={styles.paragraph}>{t('detailsPara4')}</p>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq6Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq6Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>
            <a href="https://blog.harvestr.io/payback-period" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref1')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.wallstreetprep.com/knowledge/payback-period/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref2')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://preferredcfo.com/insights/determining-payback-period-of-business-investment" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref3')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.sofi.com/learn/content/how-to-calculate-the-payback-period/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref4')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://gocardless.com/en-us/guides/posts/en-us-how-to-calculate-the-payback-period/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref5')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.calculator.net/payback-period-calculator.html" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref6')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://breakingintowallstreet.com/kb/project-finance/payback-period/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref7')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.americanexpress.com/en-us/credit-cards/credit-intel/how-to-calculate-payback-period/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref8')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}